import { useGetMyAccount } from '@queries/Account'

import type { Account, AccountTeam } from '@models/Account'
import { Perm } from '@type/permissions'

/** The two fields the decision reads; `Account` satisfies it structurally. */
export interface OnboardingAccount {
  team: Pick<AccountTeam, 'onboarded'> | null
  permissions?: Account['permissions']
}

/**
 * Whether this user belongs in the onboarding wizard rather than the app.
 *
 * Onboarding is workspace setup (name the team, invite people, vault a card,
 * pick a channel) and every write in it is gated on `team.manage_settings`.
 * Users without that permission go straight to the app; the wizard waits for
 * someone who can complete it. A user with no team at all still needs it —
 * the wizard's first step is what creates their workspace.
 *
 * `undefined` (account still loading) is "no". Callers sit behind
 * AuthRoute / AuthLayout, which do not render until /me resolves.
 */
export const needsOnboarding = (user: OnboardingAccount | undefined): boolean => {
  if (!user) return false
  if (!user.team) return true
  if (user.team.onboarded) return false
  return (user.permissions ?? []).includes(Perm.TEAM_MANAGE_SETTINGS)
}

export const useNeedsOnboarding = (): boolean => {
  const { data: user } = useGetMyAccount()
  return needsOnboarding(user)
}

export default useNeedsOnboarding
